import { findKey, isEmpty } from 'lodash';
import { ParamParseKey, generatePath } from 'react-router-dom';
import { ANY, AllowedPortal, Portals } from './content/portal/portals';
import { matchRoutes, useLocation } from './router';

export type { NavigateOptions, To } from 'react-router-dom';
export * from './router';
export { useExternalNavigate } from './utils/useExternalNavigate';

export type RouteType = Readonly<[string, AllowedPortal]>;

export const routes = {
  accountSettings: ['/account-settings', Portals.Hub],
  campusResources: ['/campus-resources', Portals.Hub],
  coaching: ['/coaching', Portals.Coaching],
  coachingAssessment: ['/coaching/assess/:assessmentType/:slide', Portals.Coaching],
  coachingChat: ['/coaching/chat', Portals.Coaching],
  coachingOnboarding: ['/coaching/onboard', Portals.Coaching],
  connectNowConfirmInfo: ['/connect-now/confirm-info', Portals.ConnectNow],
  connectNowHome: ['/connect-now', Portals.ConnectNow],
  connectNowJoinCall: ['/connect-now/join-call', Portals.ConnectNow],
  connectNowPreEncounterForm: ['/connect-now/pre-encounter-form', Portals.ConnectNow],
  consentSurveyAnalytics: ['/consent-survey-analytics', Portals.Hub],
  crisisResources: ['/crisis-resources', Portals.Hub],
  deepLinkPage: ['/deep-link', Portals.Public], // Deprecated, use loginDeepLink instead.
  findCareAssessments: ['/find-care/assess/:assessmentType/:slide', Portals.Loca],
  findCareConfirmInfo: ['/find-care/confirm-info', Portals.Loca],
  findCareCrisisSupport: ['/find-care/crisis-support/:slide', Portals.Loca],
  findCareDemographics: ['/find-care/demographics', Portals.Loca],
  findCarePreQuantitative: ['/find-care/pre-quantitative', Portals.Loca],
  findCarePreTriage: ['/find-care/pre-triage', Portals.Loca],
  findCareStartCare: ['/find-care/start', Portals.Loca],
  findCareRecommendation: ['/find-care/recommendation', Portals.Hub],
  findCareReviewAnswers: ['/find-care/review-answers', Portals.Loca],
  forwardOnPortal: ['/forward/*', Portals.Hub],
  home: ['/home', Portals.Hub],
  login: ['/login', Portals.Public],
  loginActivationResend: ['/login/activation/resend', Portals.Public],
  loginDeepLink: ['/login/deep-link', Portals.Public],
  loginFromPortal: ['/login/callback', Portals.Public],
  loginResetPassword: ['/login/reset', Portals.Public],
  loginNewPassword: ['/login/reset/new', Portals.Public],
  loginSsoResponse: ['/login/sso/response', Portals.Public],
  logout: ['/logout', Portals.Public],
  module: ['/m/:moduleId', Portals.Skills],
  mrn: ['/mrn', Portals.Public],
  mrnExternal: ['/external-mrn', Portals.Public],
  notFound: ['*', Portals.Public],
  onboarding: ['/onboarding', Portals.Hub],
  onboardingPhoneNumber: ['/onboarding/phone-number', Portals.Hub],
  onboardingTerms: ['/onboarding/terms', Portals.Hub],
  /** OnDemand is deprecated and links to connectNow */
  onDemandHome: ['/on-demand', ANY(Portals.Odes, Portals.ConnectNow)],
  onDemandConfirmInfo: ['/on-demand/confirm-info', ANY(Portals.Odes, Portals.ConnectNow)],
  onDemandJoinCall: ['/on-demand/join-call', ANY(Portals.Odes, Portals.ConnectNow)],
  onDemandPreEncounterForm: [
    '/on-demand/pre-encounter-form',
    ANY(Portals.Odes, Portals.ConnectNow),
  ],
  patientPortal: ['/patient-portal', ANY(Portals.Therapy, Portals.Psychiatry)],
  postLogin: ['/post-login', Portals.Hub],
  reflection: ['/reflection/:reflectionId', Portals.Skills],
  reflections: ['/reflections', Portals.Skills],
  reschedule: ['/reschedule/:appointmentId', ANY(Portals.Coaching, Portals.Loca)],
  surveyHome: ['/survey', Portals.SaiSurvey],
  surveyAssessments: ['/survey/:questionnaireRoute/:slide', Portals.SaiSurvey],
  surveyInterstitials: ['/survey/interstitial/:interstitialRoute', Portals.SaiSurvey],
  surveyStart: ['/survey/start', Portals.SaiSurvey],
  surveyFinish: ['/survey/finish', Portals.SaiSurvey],
  surveyResults: ['/survey/results', Portals.SaiSurvey],
  selfCareHome: ['/self-care', Portals.Skills],
  selfCareQuiz: ['/self-care/quiz/:slide', Portals.Skills],
  signup: ['/signup', Portals.Public],
  signupFinish: ['/signup/finish', Portals.Public],
  skill: ['/m/:moduleId/skill/:skillId/:slide', Portals.Skills],
  skillPathway: ['/skill/pathway', Portals.Skills],
  skillPathwaySection: ['/skill/pathway/m/:moduleId/s/:sectionId', Portals.Skills],
  start: ['/', Portals.Public],
  therapy: ['/therapy', Portals.NonLocaTherapyGateway],
  therapyEligibility: ['/therapy/eligibility', Portals.Hub],
  therapyNoSelfRefer: ['/therapy/self-refer', Portals.Hub],
  tour: ['/tour/:slide', Portals.Loca],
} as const;

export type RouteKeys = keyof typeof routes;
export type RoutableKeys = Exclude<RouteKeys, 'notFound'>;

type RoutePaths = {
  [a in keyof typeof routes]: (typeof routes)[a][0];
};

type RouteParamNames<RouteName extends RouteKeys> = ParamParseKey<RoutePaths[RouteName]>;
export type RouteParams<RouteName extends RouteKeys> = {
  [key in RouteParamNames<RouteName>]: string;
};

export const parseRouteName = (location: ReturnType<typeof useLocation>): RouteKeys => {
  const searchableRoutes = Object.values(routes).map(([routePath]) => ({ path: routePath }));

  const matches = matchRoutes(searchableRoutes, location);

  const matchedRoute = matches?.[0]?.route;
  if (!matchedRoute) {
    return 'notFound';
  }

  const matchedRouteName =
    findKey(routes, ([routePath]) => routePath === matchedRoute.path) ?? 'notFound';

  return matchedRouteName as RouteKeys;
};

export const useCurrentRouteName = (): RouteKeys => {
  const location = useLocation();

  return parseRouteName(location);
};

export const useCurrentRouteSearch = (): Record<string, string> => {
  const location = useLocation();

  const search = new URLSearchParams(location.search);

  // Convert the search entries iterator to an object.
  return Object.fromEntries(search.entries());
};

export const getRoute = <
  RouteName extends Exclude<RouteKeys, 'notFound'>,
  RouteParamValues extends RouteParams<RouteName>,
  RoutePath extends RoutePaths[RouteName],
>(
  routeName: RouteName,
  paramValues: RouteParamValues,
  searchParamValues?: ConstructorParameters<typeof URLSearchParams>[0],
): string => {
  const routePath = routes[routeName][0] as RoutePath;
  const fullPath = generatePath(routePath, paramValues);

  if (searchParamValues !== undefined) {
    const searchParams = new URLSearchParams(searchParamValues);
    return `${fullPath}?${searchParams.toString()}`;
  }

  return fullPath;
};

const isLocationPublic = (location: ReturnType<typeof useLocation>): boolean => {
  const searchableRoutes = Object.values(routes).map(([routePath]) => ({ path: routePath }));

  const matches = matchRoutes(searchableRoutes, location);

  const matchedRoute = matches?.[0]?.route;
  if (!matchedRoute) {
    return true;
  }

  const matchedRouteName = findKey(routes, ([routePath]) => routePath === matchedRoute.path);

  if (matchedRouteName === undefined) {
    // The not found page is technically public.
    return true;
  }

  const routePortal = routes[matchedRouteName as RouteKeys][1];

  return routePortal === Portals.Public;
};

export const useIsCurrentRoutePublic = (): boolean => {
  const location = useLocation();

  return isLocationPublic(location);
};

export const appendSearchToRoute = (
  fullPath: string,
  newSearchParams: Record<string, string>,
): string => {
  if (isEmpty(newSearchParams)) {
    return fullPath;
  }

  const searchParams = new URLSearchParams(newSearchParams);

  // Does not support routes with hashes...
  const appender = fullPath.includes('?') ? '&' : '?';
  return `${fullPath}${appender}${searchParams.toString()}`;
};
